import { 
  doc, 
  getDoc, 
  setDoc, 
  deleteDoc,
  collection, 
  query, 
  where, 
  getDocs,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase';

/**
 * User data model for Firestore
 * Collection: users
 * Document ID: {uid}
 * 
 * Document structure:
 * {
 *   email: string
 *   emailVerified: boolean
 *   displayName: string | null
 *   stripeCustomerId: string | null
 *   subscriptionStatus: "active" | "trialing" | "past_due" | "canceled" | "incomplete" | "none"
 *   currentPeriodEnd: string | null // ISO date
 *   createdAt: timestamp
 *   updatedAt: timestamp
 * }
 */

// User subscription status constants
export const SUBSCRIPTION_STATUS = {
  ACTIVE: 'active',
  TRIALING: 'trialing',
  PAST_DUE: 'past_due',
  CANCELED: 'canceled',
  INCOMPLETE: 'incomplete',
  NONE: 'none'
};

/**
 * Create a new user document in Firestore
 * @param {string} uid - User ID from Firebase Auth
 * @param {string} email - User's email address
 * @param {Object} additionalData - Additional user data (optional)
 * @returns {Promise<Object>} Created user document
 */
export const createUser = async (uid, email, additionalData = {}) => {
  try {
    const userRef = doc(db, 'users', uid);
    const userData = {
      email,
      emailVerified: false,
      displayName: null,
      stripeCustomerId: null,
      subscriptionStatus: SUBSCRIPTION_STATUS.NONE,
      currentPeriodEnd: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      ...additionalData
    };

    await setDoc(userRef, userData);
    console.log('User created successfully:', uid);
    return { id: uid, ...userData };
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
};

/**
 * Get user document by UID
 * @param {string} uid - User ID
 * @returns {Promise<Object|null>} User document or null if not found
 */
export const getUser = async (uid) => {
  try {
    const userRef = doc(db, 'users', uid);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      return { id: userSnap.id, ...userSnap.data() };
    } else {
      console.log('User not found:', uid);
      return null;
    }
  } catch (error) {
    console.error('Error getting user:', error);
    throw error;
  }
};

/**
 * Update user document
 * @param {string} uid - User ID
 * @param {Object} updateData - Data to update
 * @returns {Promise<Object>} Updated user document
 */
export const updateUser = async (uid, updateData) => {
  try {
    const userRef = doc(db, 'users', uid);
    const dataToUpdate = {
      ...updateData,
      updatedAt: serverTimestamp()
    };

    // Use setDoc with merge: true to create document if it doesn't exist
    await setDoc(userRef, dataToUpdate, { merge: true });
    console.log('User updated successfully:', uid);
    
    // Return updated user data
    const updatedUser = await getUser(uid);
    return updatedUser;
  } catch (error) {
    console.error('Error updating user:', error);
    throw error;
  }
};

/**
 * Update user subscription information
 * @param {string} uid - User ID
 * @param {string} stripeCustomerId - Stripe customer ID
 * @param {string} subscriptionStatus - Subscription status
 * @param {string} currentPeriodEnd - ISO date string of period end
 * @returns {Promise<Object>} Updated user document
 */
export const updateUserSubscription = async (uid, stripeCustomerId, subscriptionStatus, currentPeriodEnd, email = null) => {
  try {
    const updateData = {
      stripeCustomerId,
      subscriptionStatus,
      currentPeriodEnd
    };

    // If email is provided, include it in case we need to create a new document
    if (email) {
      updateData.email = email;
    }

    return await updateUser(uid, updateData);
  } catch (error) {
    console.error('Error updating user subscription:', error);
    throw error;
  }
};

/**
 * Get user by email
 * @param {string} email - User's email address
 * @returns {Promise<Object|null>} User document or null if not found
 */
export const getUserByEmail = async (email) => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('email', '==', email));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const userDoc = querySnapshot.docs[0];
      return { id: userDoc.id, ...userDoc.data() };
    } else {
      console.log('User not found with email:', email);
      return null;
    }
  } catch (error) {
    console.error('Error getting user by email:', error);
    throw error;
  }
};


/**
 * Get all users (admin function)
 * @returns {Promise<Array>} Array of user documents
 */
export const getAllUsers = async () => {
  try {
    const usersRef = collection(db, 'users');
    const querySnapshot = await getDocs(usersRef);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting all users:', error);
    throw error;
  }
};


/**
 * Delete user document
 * @param {string} uid - User ID
 * @returns {Promise<void>}
 */
export const deleteUser = async (uid) => {
  try {
    const userRef = doc(db, 'users', uid);
    await deleteDoc(userRef);
    console.log('User deleted successfully:', uid);
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
};
